import React, { useEffect, useCallback } from 'react';
import sdk from 'matrix-js-sdk';
import { loadSessionStorage } from './ClientSessionHandler';
import { chathubJoin, setMaximumUnreadMessageCount, updateUnreadMessages } from '../actions';
import { connect, useSelector } from 'react-redux';
import { DesktopChatBar } from '../components/chat/DesktopChatBar';
import { SizeSelector } from '../components/utilities/layouts/SizeSelector';

const SERVER_URL = process.env.REACT_APP_CHAT_SERVER || 'https://matrix.org';
const TIMELINE_LIMIT = 100;

export const MATRIX_SYNC_STATE = {
    SYNCING: 'SYNCING',
    PREPARED: 'PREPARED',
    ERROR: 'ERROR',
}

export const MATRIX_MEMBERSHIP = {
    JOIN: 'join',
    INVITE: 'invite',
}

export const MATRIX_EVENT_TYPE = {
    MESSAGE: 'm.room.message', 
    FULLY_READ: 'm.fully_read',
    ROOM_CREATE: 'm.room.create',
    ROOM_MEMBER: 'm.room.member',
}

export const maximumUnreadMessageBadgeCount = 100;

const getChatModuleInstance = () => {
    return window?.chatModuleSingleton;
};
const clearChatModuleInstance = () => {
    window['chatModuleSingleton'] = undefined;
    try{
        delete window['chatModuleSingleton'];
    }catch(e){}
};

const getUserCredentials = () => {
    const session = loadSessionStorage();
    const chathubAuth = session?.chathubAuth;
    return chathubAuth;
};

const setChatModuleInstanceProp = (key, val) => window.chatModuleSingleton[key] = val;

const init = async (updateUnreadMessageCount = null, setMaximumUnreadMessageCount = null, callback = () => {}) => {
        
    let client = null;

    if (!getChatModuleInstance() && SERVER_URL) {
        window.chatModuleSingleton = {
            client: sdk.createClient(SERVER_URL)
        }
    }

    client = getChatModuleInstance().client;

    const session = getUserCredentials();
    const credential = session?.credential;
    const user = session?.user;

    try {        
        if (!client.isLoggedIn()) {
            await client.login("org.matrix.login.jwt", {
                device_id: "NEDVC",
                token: credential,
            });
        }

        setChatModuleInstanceProp('userId', user);
        syncClient(callback, updateUnreadMessageCount, setMaximumUnreadMessageCount);
    } catch(e) {
        // show error message on failed connection.
        // with option to reconnect.
        throw e;
    }
};

const getClientSyncState = () => {
    return getChatModuleInstance()?.client?.getSyncState();
};

const syncClient = async (callback = () => {}, updateUnreadMessageCount = null, setMaximumUnreadMessageCount = null) => {

    const client = getChatModuleInstance()?.client;

    if (!client) {
        return;
    }

    if (getClientSyncState() !== 'PREPARED' && getClientSyncState() !== 'SYNCING') {
        await client.startClient({
            initialSyncLimit: TIMELINE_LIMIT
        });
    }
    
    await client.once('sync', (state, prevState, res) => {
        setupClientAndListeners(state, updateUnreadMessageCount, setMaximumUnreadMessageCount, callback);
    });
};

export const setupClientAndListeners = (state = getClientSyncState(), updateUnreadMessageCount = null, setMaximumUnreadMessageCount = null, callback = () => {}) => {

    const client = getChatModuleInstance()?.client;
    const userId = getChatModuleInstance()?.userId;
    let fullyReadMarkerMessageId = '';

    const rooms = client.getRooms();

    // if room id is created and stored somewhere else, this block isn't needed
    const myroom = rooms.find(
        room => room.roomId === getUserCredentials()?.roomId && room.getJoinedMembers().find(v => v.userId === userId)
    );

    if (!myroom) {
        return;
    }

    // set latest Matrix Events in the Room's Timeline
    // Latest to the past 100 messages (can be set)
    console.log(myroom?.timeline) // TODO: remove

    callback({
        client,
        events: myroom?.timeline,
        otherUsers: myroom.getJoinedMembers().filter(v => v.userId !== userId),
        roomId: myroom.roomId,
        userId
    });

    const timelineListener = (_event, room, _toStartOfTimeline, _removed, _data) => {
        if (room.roomId === myroom.roomId) {
            let lastReadMessageId = fullyReadMarkerMessageId;
            if (!lastReadMessageId) {
                lastReadMessageId = room.getEventReadUpTo(userId);
            }
            const timeline = room.getLiveTimeline().getEvents();
            const unreadMessages = getUnreadMessages(lastReadMessageId, timeline, userId);
            const unreadMessagesCount = unreadMessages.length;
            updateUnreadMessageCount(unreadMessagesCount);    
        }
    };
    const fullyReadListener = (event, room, _oldEvent) => {
        if (room.roomId === myroom.roomId && event.getType() === MATRIX_EVENT_TYPE.FULLY_READ) {
            fullyReadMarkerMessageId = event.getContent().event_id;
            const timeline = room.getLiveTimeline().getEvents();
            const unreadMessages = getUnreadMessages(fullyReadMarkerMessageId, timeline, userId);
            const unreadMessagesCount = unreadMessages.length;
            updateUnreadMessageCount(unreadMessagesCount);
        }
    };

    if (state === MATRIX_SYNC_STATE.PREPARED) {
        const fullyReadMessage = myroom.getAccountData(MATRIX_EVENT_TYPE.FULLY_READ);
        fullyReadMarkerMessageId = fullyReadMessage?.getContent()?.event_id;
        const timeline = myroom?.getLiveTimeline()?.getEvents();
        const unreadMessages = getUnreadMessages(fullyReadMarkerMessageId, timeline, userId);
        const unreadMessagesCount = unreadMessages?.length;
        updateUnreadMessageCount(unreadMessagesCount);

        updateMaxUnreadMessageCount(timeline, unreadMessages, userId, setMaximumUnreadMessageCount);
        
        client
            .removeListener('Room.timeline', timelineListener)
            .on('Room.timeline', timelineListener);      
        client
            .removeListener('Room.accountData', fullyReadListener)
            .on('Room.accountData', fullyReadListener);
    }
}

export const isNewMessage = (event, userId) => {
    if (event.getSender() === userId){
        return false;
    } else if (event.isRedacted()) {
        return false;
    } else {
        return event.getType() === MATRIX_EVENT_TYPE.MESSAGE
            ? event.isRelation('m.replace') 
                ? false 
                : true
            : false;    
    }
};

export const getUnreadMessages = (lastReadMessageId, timeline, userId) => {
    let unreadMessages = timeline.filter((event) => event?.getType() === MATRIX_EVENT_TYPE.MESSAGE);
    if (lastReadMessageId) {
        const lastReadMessageIndex = unreadMessages.findIndex(timelineEvent => 
            timelineEvent.getId() === lastReadMessageId
        );
        unreadMessages = unreadMessages.filter((event, index) => {
            if (index > lastReadMessageIndex) {
                return isNewMessage(event, userId);
            } else {
                return false;
            }
        });
    }
    return unreadMessages;
};

const updateMaxUnreadMessageCount = (timeline, unreadMessages, userId, setMaxUnreadMessageCount) => {
    if (timeline.length >= TIMELINE_LIMIT) {
        const firstNewMessage = timeline.find((event) => {
            return isNewMessage(event, userId);
        });
        
        // If the first new message that is synced is unread, assume that earlier messages 
        // that are not yet synced may also be unread
        if (unreadMessages.includes(firstNewMessage)) {
            let maxUnreadMessageCount = unreadMessages.length-1;
            setMaxUnreadMessageCount(maxUnreadMessageCount); 
        }    
    }
};

export const updateFullyReadMarker = async(lastReadMessage, roomId, client, setMaxUnreadMessageCount) => {
    const lastReadMessageId = lastReadMessage.getId();
    if (lastReadMessage.getRoomId() === roomId) {
        await client.setRoomReadMarkers(roomId, lastReadMessageId, lastReadMessage);
        setMaxUnreadMessageCount(maximumUnreadMessageBadgeCount);
    }
};

export async function chatLogOut() {
    const chatModuleInstance = getChatModuleInstance();
    if (chatModuleInstance) {
        clearChatModuleInstance();

        if(chatModuleInstance.client) {
            chatModuleInstance.client.stopClient();
            return await chatModuleInstance.client?.logout();
        }
    }
}

export const maxChatWidth = theme => {
    return theme.spacing(41.5);
}

function ChatProviderComponent({ children, updateUnreadMessageCount, setMaxUnreadMessageCount, onChathubJoin }) {

    const hasCCToken = useSelector(state => state?.chat?.hasCCToken);
    const hasMatrixToken = useSelector(state => state?.chat?.hasMatrixToken);

    const initChat = useCallback(async () => {
        if (getUserCredentials()) {
            await init(updateUnreadMessageCount, setMaxUnreadMessageCount);
        }
    }, [updateUnreadMessageCount, setMaxUnreadMessageCount]);

    useEffect(() => {
        if (hasCCToken && !hasMatrixToken) {
            onChathubJoin();
        }
    }, [onChathubJoin, hasCCToken, hasMatrixToken]);

    useEffect(() => {
        if (!getChatModuleInstance() && hasMatrixToken) {
            initChat();
        }
    }, [initChat, hasMatrixToken]);

    return <>
        {children}
        {
            /* user has to  be logged in to see the desktop chat bar */
            hasCCToken && <SizeSelector desktop={<DesktopChatBar />} />
        }
    </>;
}

const mapDispatchToProps = dispatch => {
    const updateUnreadMessageCount = (unreadMessageCount) => {
        dispatch(updateUnreadMessages(unreadMessageCount));
    };
    const setMaxUnreadMessageCount = (maximumUnreadMessageCount) => {
        dispatch(setMaximumUnreadMessageCount(maximumUnreadMessageCount));
    };
    const onChathubJoin = () => {
        dispatch(chathubJoin());
    }
    return {
        updateUnreadMessageCount,
        setMaxUnreadMessageCount,
        onChathubJoin,
    };
};

export const ChatProvider = connect(null, mapDispatchToProps)(ChatProviderComponent);

const Chat = {
    getChatModuleInstance,
    getClientSyncState,
    init,
    syncClient,
    setupClientAndListeners,
};

export default Chat;